import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/layout/DataTable";
import { EmptyState } from "../../components/layout/EmptyState";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { apiFetch } from "../../lib/api";
import { formatPct } from "../../lib/format";
import { patchSearchParams, readStringParam } from "../../lib/url-state";
import type {
  FeedbackOverviewResponse,
  RecommendationRunFeedbackResponse,
  RecommendationRunSummary,
  RunsListResponse,
  SkuFeedbackDetailResponse,
  SupplierFeedbackListResponse,
  SupplierFeedbackRow,
} from "../../lib/types";
import { useDomainVersion } from "../../app/invalidation";

type FeedbackOverviewSnapshot = {
  overview: FeedbackOverviewResponse;
  supplierFeedback: SupplierFeedbackRow[];
  recommendationRuns: RecommendationRunSummary[];
};

export function FeedbackWorkspace() {
  const feedbackVersion = useDomainVersion("feedback");
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [runFeedbackError, setRunFeedbackError] = useState<string | null>(null);
  const [skuFeedbackError, setSkuFeedbackError] = useState<string | null>(null);
  const [overview, setOverview] = useState<FeedbackOverviewResponse | null>(null);
  const [supplierFeedback, setSupplierFeedback] = useState<SupplierFeedbackRow[]>([]);
  const [recommendationRuns, setRecommendationRuns] = useState<RecommendationRunSummary[]>([]);
  const [runFeedback, setRunFeedback] = useState<RecommendationRunFeedbackResponse | null>(null);
  const [skuFeedback, setSkuFeedback] = useState<SkuFeedbackDetailResponse | null>(null);
  const [skuFeedbackLoading, setSkuFeedbackLoading] = useState(false);
  const [runFeedbackLoading, setRunFeedbackLoading] = useState(false);
  const runFeedbackRequestSeqRef = useRef(0);
  const overviewSnapshotRequestSeqRef = useRef(0);
  const skuFeedbackRequestSeqRef = useRef(0);

  const supplier = readStringParam(searchParams, "supplier");
  const sku = readStringParam(searchParams, "sku");
  const runId = readStringParam(searchParams, "runId");

  async function fetchOverviewSnapshot(): Promise<FeedbackOverviewSnapshot> {
    const [overviewPayload, supplierPayload, runsPayload] = await Promise.all([
      apiFetch<FeedbackOverviewResponse>("/feedback/overview?window_days=90"),
      apiFetch<SupplierFeedbackListResponse>("/feedback/suppliers?window_days=90"),
      apiFetch<RunsListResponse>("/runs/recommendations?limit=20"),
    ]);

    return {
      overview: overviewPayload,
      supplierFeedback: supplierPayload.items || [],
      recommendationRuns: runsPayload.items || [],
    };
  }

  useEffect(() => {
    const requestId = ++overviewSnapshotRequestSeqRef.current;
    setLoading(true);
    setOverviewError(null);
    fetchOverviewSnapshot()
      .then((snapshot) => {
        if (requestId !== overviewSnapshotRequestSeqRef.current) {
          return;
        }
        setOverview(snapshot.overview);
        setSupplierFeedback(snapshot.supplierFeedback);
        setRecommendationRuns(snapshot.recommendationRuns || []);
        setOverviewError(null);
      })
      .catch((err) => {
        if (requestId !== overviewSnapshotRequestSeqRef.current) {
          return;
        }
        setOverviewError((err as Error).message);
      })
      .finally(() => {
        if (requestId !== overviewSnapshotRequestSeqRef.current) {
          return;
        }
        setLoading(false);
      });
  }, [feedbackVersion]);

  useEffect(() => {
    const requestId = ++runFeedbackRequestSeqRef.current;
    setRunFeedbackError(null);
    if (!runId) {
      setRunFeedback(null);
      setRunFeedbackLoading(false);
      return;
    }
    setRunFeedback(null);
    setRunFeedbackLoading(true);
    apiFetch<RecommendationRunFeedbackResponse>(`/feedback/runs/recommendations/${encodeURIComponent(runId)}`)
      .then((payload) => {
        if (requestId !== runFeedbackRequestSeqRef.current) return;
        setRunFeedback(payload);
      })
      .catch((err) => {
        if (requestId !== runFeedbackRequestSeqRef.current) return;
        setRunFeedbackError((err as Error).message);
      })
      .finally(() => {
        if (requestId !== runFeedbackRequestSeqRef.current) return;
        setRunFeedbackLoading(false);
      });
  }, [runId]);

  useEffect(() => {
    const requestId = ++skuFeedbackRequestSeqRef.current;
    setSkuFeedbackError(null);
    if (!sku) {
      setSkuFeedback(null);
      setSkuFeedbackLoading(false);
      return;
    }
    setSkuFeedback(null);
    setSkuFeedbackLoading(true);
    apiFetch<SkuFeedbackDetailResponse>(`/feedback/skus/${encodeURIComponent(sku)}?window_days=90`)
      .then((payload) => {
        if (requestId !== skuFeedbackRequestSeqRef.current) {
          return;
        }
        setSkuFeedback(payload);
        setSkuFeedbackError(null);
      })
      .catch((err) => {
        if (requestId !== skuFeedbackRequestSeqRef.current) {
          return;
        }
        setSkuFeedback(null);
        setSkuFeedbackError((err as Error).message);
      })
      .finally(() => {
        if (requestId !== skuFeedbackRequestSeqRef.current) {
          return;
        }
        setSkuFeedbackLoading(false);
      });
  }, [sku]);

  const filteredRows = supplier
    ? supplierFeedback.filter((row) => row.supplier.toLowerCase().includes(supplier.toLowerCase()))
    : supplierFeedback;
  const selectedRun = recommendationRuns.find((run) => run.run_id === runId) || null;
  const runOutcomeRows = runFeedback?.outcomes || [];
  const topMisses = useMemo(
    () => [...runOutcomeRows]
      .filter((row) => ['late', 'early'].includes(row.stockout_status || '') || ['late', 'not_received'].includes(row.arrival_status || ''))
      .sort((a, b) => Math.abs((b.stockout_error_days || b.arrival_delay_days || 0)) - Math.abs((a.stockout_error_days || a.arrival_delay_days || 0)))
      .slice(0, 8),
    [runOutcomeRows],
  );
  const topWins = useMemo(
    () => [...runOutcomeRows]
      .filter((row) => row.stockout_status === 'prevented_by_replenishment' || row.order_execution_status === 'on_time' || row.arrival_status === 'on_time')
      .slice(0, 8),
    [runOutcomeRows],
  );
  const runTrustSummary = useMemo(() => {
    if (!runFeedback) return null;
    const stockoutAccurate = (runFeedback.summary.stockout_status_counts.accurate || 0) + (runFeedback.summary.stockout_status_counts.prevented_by_replenishment || 0);
    const lateArrivals = runFeedback.summary.arrival_status_counts.late || 0;
    const onTimeOrders = runFeedback.summary.order_execution_counts.on_time || 0;
    const totalOutcomes = runFeedback.outcomes.length || 1;
    const trustScore = Math.max(0, Math.min(100, Math.round(((stockoutAccurate + onTimeOrders - lateArrivals) / totalOutcomes) * 100)));
    let label = 'Low';
    if (trustScore >= 70) label = 'High';
    else if (trustScore >= 40) label = 'Medium';
    return { trustScore, label };
  }, [runFeedback]);
  const runExecutiveSummary = useMemo(() => {
    if (!runFeedback || !selectedRun) return [] as string[];
    const highlights: string[] = [];
    const totalOutcomes = runFeedback.outcomes.length;
    const prevented = runFeedback.summary.stockout_status_counts.prevented_by_replenishment || 0;
    const lateStockouts = runFeedback.summary.stockout_status_counts.late || 0;
    const earlyStockouts = runFeedback.summary.stockout_status_counts.early || 0;
    const onTimeOrders = runFeedback.summary.order_execution_counts.on_time || 0;
    const lateArrivals = runFeedback.summary.arrival_status_counts.late || 0;
    const avgBias = formatPct(runFeedback.summary.avg_abs_velocity_bias_pct);

    highlights.push(`${selectedRun.run_type} run reviewed across ${totalOutcomes} measured outcomes.`);
    highlights.push(`Average absolute velocity bias was ${avgBias}.`);
    if (prevented > 0) highlights.push(`${prevented} potential stockouts were prevented by replenishment actions.`);
    if (onTimeOrders > 0) highlights.push(`${onTimeOrders} orders executed on time in the measured window.`);
    if (lateStockouts > 0 || earlyStockouts > 0) highlights.push(`Stockout misses remain: ${earlyStockouts} early and ${lateStockouts} late.`);
    if (lateArrivals > 0) highlights.push(`${lateArrivals} arrivals landed late and reduced timing confidence for parts of this run.`);
    return highlights.slice(0, 5);
  }, [runFeedback, selectedRun]);
  const formatStatusCounts = (counts: Record<string, number>) =>
    Object.entries(counts)
      .filter(([, value]) => value > 0)
      .map(([key, value]) => `${key.split('_').join(' ')}: ${value}`)
      .join(' · ') || 'No data';
  const warnings = [overviewError, runFeedbackError, skuFeedbackError].filter((warning): warning is string => Boolean(warning));

  return (
    <>
      {overview && (
        <Panel className="status-strip">
          {[30, 90].map((window) => {
            const metric = overview.windows[String(window)];
            if (!metric) return null;
            return (
              <div key={`feedback-window-${window}`} className="status-chip wide-chip">
                <strong>{window}d Accuracy</strong>
                <span>
                  Velocity {formatPct(metric.avg_abs_velocity_bias_pct)} | Orders {formatPct(metric.order_on_time_rate)} |
                  Early stockouts {metric.early_stockout_count} | Late arrivals {metric.late_arrival_count}
                </span>
              </div>
            );
          })}
          <MetricCard label="Open Suggestions" value={overview.open_suggestions.length} />
        </Panel>
      )}

      <Panel>
        <h2>Review & Learn</h2>
        <p className="muted-small">See what worked, what needs attention, and where the system is improving over time.</p>
        <div className="form-row">
          <label>
            Review period
            <select value={runId} onChange={(event) => patchSearchParams(searchParams, setSearchParams, { runId: event.target.value || null })}>
              <option value="">Select a run</option>
              {recommendationRuns.map((run) => (
                <option key={run.run_id} value={run.run_id}>
                  {run.run_at} · {run.run_type} · {run.run_health_status || 'unknown'}
                </option>
              ))}
            </select>
          </label>
        </div>
        {selectedRun && !runFeedbackLoading && (
          <div className="detail-cards">
            <MetricCard label="Run Timestamp" value={selectedRun.run_at} />
            <MetricCard label="Run Type" value={selectedRun.run_type} />
            <MetricCard label="Health" value={selectedRun.run_health_status || '-'} />
            <MetricCard label="Fresh / Blocked SKUs" value={`${selectedRun.fresh_sku_count ?? 0} / ${selectedRun.blocked_sku_count ?? 0}`} />
          </div>
        )}
        {!runId && (
          <EmptyState
            title="Select a recommendation run"
            message="Choose a run to inspect quality signals, misses, wins, and SKU-level follow-up evidence."
            compact
          />
        )}
        {runFeedback && (
          <>
            <div className="detail-cards">
              <MetricCard label="Velocity Samples" value={runFeedback.summary.velocity_samples} />
              <MetricCard label="Avg Abs Velocity Bias" value={formatPct(runFeedback.summary.avg_abs_velocity_bias_pct)} />
              <MetricCard label="Outcomes" value={runFeedback.outcomes.length} />
              <MetricCard label="Horizons" value={runFeedback.horizons.length} />
            </div>
            <div className="detail-cards">
              <MetricCard label="Accurate / Prevented Stockouts" value={`${runFeedback.summary.stockout_status_counts.accurate || 0} / ${runFeedback.summary.stockout_status_counts.prevented_by_replenishment || 0}`} />
              <MetricCard label="On-Time Orders" value={runFeedback.summary.order_execution_counts.on_time || 0} />
              <MetricCard label="Late Arrivals" value={runFeedback.summary.arrival_status_counts.late || 0} />
              <MetricCard label="On-Time Transfers" value={runFeedback.summary.transfer_execution_counts.on_time || 0} />
            </div>
            {runTrustSummary && (
              <Panel>
                <h3>Run Trust Summary</h3>
                <div className="detail-cards">
                  <MetricCard label="Trust Score" value={`${runTrustSummary.trustScore}%`} hint="Composite run-confidence signal" />
                  <MetricCard label="Trust Level" value={runTrustSummary.label} hint="Fast operator read on recommendation reliability" />
                  <MetricCard label="Early Stockouts" value={runFeedback.summary.stockout_status_counts.early || 0} />
                  <MetricCard label="Late Stockouts" value={runFeedback.summary.stockout_status_counts.late || 0} />
                </div>
                <div className="status-strip">
                  <div className="status-chip wide-chip"><strong>Stockouts</strong><span>{formatStatusCounts(runFeedback.summary.stockout_status_counts)}</span></div>
                  <div className="status-chip wide-chip"><strong>Orders</strong><span>{formatStatusCounts(runFeedback.summary.order_execution_counts)}</span></div>
                  <div className="status-chip wide-chip"><strong>Arrivals</strong><span>{formatStatusCounts(runFeedback.summary.arrival_status_counts)}</span></div>
                  <div className="status-chip wide-chip"><strong>Transfers</strong><span>{formatStatusCounts(runFeedback.summary.transfer_execution_counts)}</span></div>
                </div>
              </Panel>
            )}
            {runExecutiveSummary.length > 0 && (
              <Panel>
                <h3>Executive Summary</h3>
                <ul className="summary-list">
                  {runExecutiveSummary.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Panel>
            )}
            <Panel>
              <h3>Top Misses</h3>
              <DataTable slim>
                <table>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Stockout</th>
                      <th>Arrival</th>
                      <th>Slip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topMisses.length > 0 ? topMisses.map((row) => (
                      <tr key={`miss-${row.sku}`} onClick={() => patchSearchParams(searchParams, setSearchParams, { sku: row.sku })}>
                        <td>{row.sku}</td>
                        <td>{row.stockout_status || '-'}</td>
                        <td>{row.arrival_status || '-'}</td>
                        <td>{row.stockout_error_days ?? row.arrival_delay_days ?? '-'}</td>
                      </tr>
                    )) : <tr><td colSpan={4}>No major misses yet.</td></tr>}
                  </tbody>
                </table>
              </DataTable>
            </Panel>
            <Panel>
              <h3>Top Wins</h3>
              <DataTable slim>
                <table>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Stockout</th>
                      <th>Order</th>
                      <th>Arrival</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topWins.length > 0 ? topWins.map((row) => (
                      <tr key={`win-${row.sku}`} onClick={() => patchSearchParams(searchParams, setSearchParams, { sku: row.sku })}>
                        <td>{row.sku}</td>
                        <td>{row.stockout_status || '-'}</td>
                        <td>{row.order_execution_status || '-'}</td>
                        <td>{row.arrival_status || '-'}</td>
                      </tr>
                    )) : <tr><td colSpan={4}>No standout wins yet.</td></tr>}
                  </tbody>
                </table>
              </DataTable>
            </Panel>
            <DataTable>
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Stockout</th>
                    <th>Arrival</th>
                    <th>Order</th>
                    <th>Transfer</th>
                    <th>Lineage</th>
                  </tr>
                </thead>
                <tbody>
                  {runFeedback.outcomes.slice(0, 15).map((row) => (
                    <tr key={`run-feedback-${row.sku}`} onClick={() => patchSearchParams(searchParams, setSearchParams, { sku: row.sku })}>
                      <td>{row.sku}</td>
                      <td>{row.stockout_status || '-'}</td>
                      <td>{row.arrival_status || '-'}</td>
                      <td>{row.order_execution_status || '-'}</td>
                      <td>{row.transfer_execution_status || '-'}</td>
                      <td>{row.lineage_mode || '-'}{row.lineage_confidence != null ? ` (${(row.lineage_confidence * 100).toFixed(0)}%)` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>
          </>
        )}
        {runId && runFeedbackLoading && <p>Loading run feedback…</p>}
        {runId && !runFeedbackLoading && !runFeedback && !runFeedbackError && (
          <EmptyState
            title="No run feedback available"
            message="This run has no feedback rows yet, or the evaluation pipeline has not produced run-level outcomes."
            compact
          />
        )}
      </Panel>

      <Panel>
        <h2>Supplier Feedback Scorecards</h2>
        <div className="form-row">
          <label>
            Supplier
            <input value={supplier} onChange={(event) => patchSearchParams(searchParams, setSearchParams, { supplier: event.target.value || null })} />
          </label>
          <label>
            SKU
            <input value={sku} onChange={(event) => patchSearchParams(searchParams, setSearchParams, { sku: event.target.value || null })} />
          </label>
        </div>
        <DataTable>
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Median Delay</th>
                <th>P90 Delay</th>
                <th>On-Time Orders</th>
                <th>Late Orders</th>
                <th>Stockout Miss Rate</th>
                <th>Velocity Bias</th>
                <th>A-SKU Misses</th>
                <th>Cash Warnings</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={`supplier-feedback-${row.supplier}`} onClick={() => patchSearchParams(searchParams, setSearchParams, { supplier: row.supplier })}>
                  <td>{row.supplier}</td>
                  <td>{row.median_arrival_delay_days ?? "-"}</td>
                  <td>{row.p90_arrival_delay_days ?? "-"}</td>
                  <td>{formatPct(row.order_on_time_rate)}</td>
                  <td>{formatPct(row.late_order_rate)}</td>
                  <td>{formatPct(row.stockout_miss_rate)}</td>
                  <td>{formatPct(row.avg_velocity_bias_pct)}</td>
                  <td>{row.a_sku_early_stockout_count}</td>
                  <td>{row.cash_warning_count}</td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={9}>No supplier feedback rows match the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </DataTable>
      </Panel>

      {skuFeedback && (
        <Panel>
          <h2>SKU Feedback: {skuFeedback.sku}</h2>
          <div className="detail-cards">
            <MetricCard label="Horizon Rows" value={(skuFeedback.horizons || []).length} />
            <MetricCard label="Execution Outcomes" value={(skuFeedback.outcomes || []).length} />
            <MetricCard label="Suggestions" value={(skuFeedback.suggestions || []).length} />
          </div>

          <DataTable slim>
            <table>
              <thead>
                <tr>
                  <th>Suggestion</th>
                  <th>Current</th>
                  <th>Suggested</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {(skuFeedback.suggestions || []).length > 0 ? (
                  (skuFeedback.suggestions || []).map((suggestion) => (
                    <tr key={suggestion.id}>
                      <td>{suggestion.suggestion_type}</td>
                      <td>{suggestion.current_value ?? "-"}</td>
                      <td>{suggestion.suggested_value ?? "-"}</td>
                      <td>{(suggestion.confidence * 100).toFixed(0)}%</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>No open SKU suggestions.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </DataTable>
        </Panel>
      )}

      {sku && skuFeedbackLoading && (
        <Panel>
          <p>Loading SKU feedback…</p>
        </Panel>
      )}

      {warnings.length > 0 && <WarningPanel title="Feedback Errors" warnings={warnings} />}
      {loading && <Panel><p>Loading feedback…</p></Panel>}
    </>
  );
}
